import { supabase } from '../lib/supabase';
import { getAssetPublicUrl } from './avatarAssetService';

// ─── STUDENT-FACING ASSET LOADING ─────────────────────────────────────────────

export const getStudentAssetsByCategory = async (categorySlug) => {
  const { data: cat } = await supabase?.from('avatar_asset_categories')?.select('id')?.eq('slug', categorySlug)?.single();

  if (!cat?.id) return [];

  const { data, error } = await supabase?.from('avatar_assets')?.select('*')?.eq('category_id', cat?.id)?.eq('is_active', true)?.eq('is_archived', false)?.eq('is_student_visible', true)?.order('sort_order', { ascending: true });

  if (error) throw error;

  return (data || [])?.map(asset => {
    const baseUrl = getAssetPublicUrl(asset?.storage_path);
    const cacheBust = asset?.updated_at ? new Date(asset.updated_at)?.getTime() : asset?.created_at ? new Date(asset.created_at)?.getTime() : 0;
    return {
      ...asset,
      publicUrl: cacheBust ? `${baseUrl}?v=${cacheBust}` : baseUrl,
    };
  });
};

export const getAllStudentAssets = async () => {
  const { data, error } = await supabase?.from('avatar_assets')?.select(`*, avatar_asset_categories (id, slug, label, layer_order)`)?.eq('is_active', true)?.eq('is_archived', false)?.eq('is_student_visible', true)?.order('layer_order', { ascending: true })?.order('sort_order', { ascending: true });

  if (error) throw error;

  return (data || [])?.map(asset => {
    const baseUrl = getAssetPublicUrl(asset?.storage_path);
    const cacheBust = asset?.updated_at ? new Date(asset.updated_at)?.getTime() : asset?.created_at ? new Date(asset.created_at)?.getTime() : 0;
    const publicUrl = cacheBust ? `${baseUrl}?v=${cacheBust}` : baseUrl;

    // Resolve hair_back public URL for hair_styles assets
    let hairBackPublicUrl = null;
    if (asset?.hair_back_storage_path) {
      const backBase = getAssetPublicUrl(asset?.hair_back_storage_path);
      hairBackPublicUrl = cacheBust ? `${backBase}?v=${cacheBust}` : backBase;
    }

    // Resolve body_head public URL for bodies assets
    let bodyHeadPublicUrl = null;
    if (asset?.body_head_storage_path) {
      const headBase = getAssetPublicUrl(asset?.body_head_storage_path);
      bodyHeadPublicUrl = cacheBust ? `${headBase}?v=${cacheBust}` : headBase;
    }

    return {
      ...asset,
      publicUrl,
      hair_back_public_url: hairBackPublicUrl,
      body_head_public_url: bodyHeadPublicUrl,
    };
  });
};

// ─── USER AVATAR CONFIG ────────────────────────────────────────────────────────

export const getUserAvatarConfig = async (userId) => {
  const { data, error } = await supabase?.from('user_avatar_configs')?.select('*')?.eq('user_id', userId)?.eq('is_active', true)?.order('updated_at', { ascending: false })?.limit(1)?.maybeSingle();

  if (error) throw error;
  return data;
};

export const saveUserAvatarConfig = async (userId, avatarConfig) => {
  // Deactivate existing configs
  await supabase?.from('user_avatar_configs')?.update({ is_active: false })?.eq('user_id', userId);

  // Insert new active config
  const { data, error } = await supabase?.from('user_avatar_configs')?.insert({
      user_id: userId,
      avatar_config: avatarConfig,
      is_active: true,
    })?.select()?.single();

  if (error) throw error;
  return data;
};

export const awardAvatarFirstSaveXP = async (userId) => {
  const { data: profile, error: fetchError } = await supabase
    ?.from('user_profiles')
    ?.select('xp_points, avatar_xp_awarded')
    ?.eq('id', userId)
    ?.single();

  if (fetchError) throw fetchError;

  // Idempotency guard — only award once
  if (profile?.avatar_xp_awarded) {
    return profile?.xp_points || 0;
  }

  const currentXP = profile?.xp_points || 0;
  const newXP = currentXP + 50;
  const newLevel = Math.floor(newXP / 100) + 1;

  const { error: updateError } = await supabase
    ?.from('user_profiles')
    ?.update({
      xp_points: newXP,
      level: newLevel,
      avatar_xp_awarded: true,
      updated_at: new Date()?.toISOString(),
    })
    ?.eq('id', userId);

  if (updateError) throw updateError;

  // Award "Avatar Creator" badge
  await awardBadgeIfNotExists(userId, 'avatar', 'Avatar Creator', 'Created your first Shakespearean avatar');

  return newXP;
};

// ─── BADGE HELPERS ────────────────────────────────────────────────────────────

/**
 * Award a badge to a user if they don't already have it.
 * Uses the existing award_achievement_badge SQL function.
 */
export const awardBadgeIfNotExists = async (userId, badgeType, badgeName, badgeDescription) => {
  try {
    const { error } = await supabase?.rpc('award_achievement_badge', {
      p_user_id: userId,
      p_badge_type: badgeType,
      p_badge_name: badgeName,
      p_badge_description: badgeDescription,
      p_goal_id: null,
    });
    if (error) console.error('Badge award error:', error);
  } catch (err) {
    console.error('awardBadgeIfNotExists error:', err);
  }
};

/**
 * Check XP milestones and award corresponding badges.
 * Call this after any XP update.
 */
export const checkXpMilestoneBadges = async (userId, newXp) => {
  const milestones = [
    { xp: 100,  type: 'xp', name: 'Century Scholar',   desc: 'Earned 100 XP' },
    { xp: 250,  type: 'xp', name: 'Rising Star',        desc: 'Earned 250 XP' },
    { xp: 500,  type: 'xp', name: 'Stage Veteran',      desc: 'Earned 500 XP' },
    { xp: 1000, type: 'xp', name: 'Bard\'s Champion',   desc: 'Earned 1000 XP' },
  ];
  for (const m of milestones) {
    if (newXp >= m?.xp) {
      await awardBadgeIfNotExists(userId, m?.type, m?.name, m?.desc);
    }
  }
};

/**
 * Award XP earned from a quiz completion.
 * Increments user_profiles.xp_points, awards quiz-specific badges,
 * runs milestone badge checks, and returns newly unlocked milestone badges.
 *
 * @param {string} userId
 * @param {number} xpAmount - total XP earned this quiz
 * @param {Array}  quizBadges - badges earned from quiz logic (Perfect Score, Shakespeare Scholar, etc.)
 * @returns {{ newXP: number, milestoneBadges: Array }}
 */
export const awardQuizXP = async (userId, xpAmount, quizBadges = []) => {
  if (!userId || !xpAmount || xpAmount <= 0) return { newXP: 0, milestoneBadges: [] };

  // Fetch current XP
  const { data: profile, error: fetchError } = await supabase
    ?.from('user_profiles')
    ?.select('xp_points')
    ?.eq('id', userId)
    ?.single();

  if (fetchError) throw fetchError;

  const currentXP = profile?.xp_points || 0;
  const newXP = currentXP + xpAmount;
  const newLevel = Math.floor(newXP / 100) + 1;

  // Persist to Supabase
  const { error: updateError } = await supabase
    ?.from('user_profiles')
    ?.update({
      xp_points: newXP,
      level: newLevel,
      updated_at: new Date()?.toISOString(),
    })
    ?.eq('id', userId);

  if (updateError) throw updateError;

  // Award quiz-specific badges (Perfect Score, Shakespeare Scholar, etc.)
  for (const badge of quizBadges) {
    await awardBadgeIfNotExists(userId, 'quiz', badge?.name, badge?.description);
  }

  // Determine which milestone thresholds were just crossed
  const milestones = [
    { xp: 250,  type: 'xp', name: 'Rising Star',      desc: 'Earned 250 XP' },
    { xp: 500,  type: 'xp', name: 'Stage Veteran',    desc: 'Earned 500 XP' },
    { xp: 1000, type: 'xp', name: 'Bard\'s Champion', desc: 'Earned 1000 XP' },
  ];

  const newlyUnlocked = [];
  for (const m of milestones) {
    if (currentXP < m?.xp && newXP >= m?.xp) {
      await awardBadgeIfNotExists(userId, m?.type, m?.name, m?.desc);
      newlyUnlocked?.push({ name: m?.name, description: m?.desc });
    }
  }

  // Full milestone check (covers 100 XP threshold too)
  await checkXpMilestoneBadges(userId, newXP);

  return { newXP, milestoneBadges: newlyUnlocked };
};

export const updateUserAvatarConfig = async (configId, avatarConfig) => {
  const { data, error } = await supabase?.from('user_avatar_configs')?.update({ avatar_config: avatarConfig, updated_at: new Date()?.toISOString() })?.eq('id', configId)?.select()?.single();
  if (error) throw error;
  return data;
};

export const getAssetOwnershipPercentages = async () => {
  // Fetch all active avatar configs to compute per-asset ownership %
  const { data: configs, error } = await supabase
    ?.from('user_avatar_configs')
    ?.select('avatar_config')
    ?.eq('is_active', true);
  if (error) return {};

  const totalUsers = configs?.length || 0;
  if (totalUsers === 0) return {};

  const slugCounts = {};
  for (const row of configs) {
    const cfg = row?.avatar_config;
    if (!cfg || typeof cfg !== 'object') continue;
    for (const val of Object.values(cfg)) {
      if (val && typeof val === 'string') {
        slugCounts[val] = (slugCounts?.[val] || 0) + 1;
      }
    }
  }

  const percentages = {};
  for (const [slug, count] of Object.entries(slugCounts)) {
    percentages[slug] = Math.round((count / totalUsers) * 100);
  }
  return percentages;
};

// ─── UNLOCK REQUIREMENT HELPERS ───────────────────────────────────────────────

/**
 * Load the data needed to evaluate asset unlock requirements for a user:
 * their XP, earned badges, and earned achievement IDs.
 */
export const getUserUnlockData = async (userId) => {
  if (!userId) return { xp: 0, badges: [], achievementIds: new Set() };

  const [profileResult, badgesResult, achievementsResult] = await Promise.all([
    supabase?.from('user_profiles')?.select('xp_points')?.eq('id', userId)?.single(),
    supabase?.from('achievement_badges')?.select('badge_type, badge_name')?.eq('user_id', userId),
    supabase?.from('user_achievements')?.select('achievement_id')?.eq('user_id', userId),
  ]);

  const xp = profileResult?.data?.xp_points || 0;
  const badges = badgesResult?.data || [];
  const achievementIds = new Set((achievementsResult?.data || [])?.map(a => a?.achievement_id));

  return { xp, badges, achievementIds };
};

/**
 * Check whether a single asset is unlocked for a user given their unlock data.
 * Returns true if unlocked, false if locked.
 */
export const isAssetUnlocked = (asset, unlockData) => {
  const type = asset?.unlock_requirement_type || 'none';
  if (type === 'none') return true;

  const { xp = 0, badges = [], achievementIds = new Set() } = unlockData || {};

  if (type === 'xp') {
    return xp >= (asset?.unlock_xp_required || 0);
  }

  if (type === 'badge') {
    return badges?.some(
      b => b?.badge_type === asset?.unlock_badge_type && b?.badge_name === asset?.unlock_badge_name
    );
  }

  if (type === 'achievement') {
    return achievementIds?.has(asset?.unlock_achievement_id);
  }

  return true;
};

/**
 * Get a human-readable description of an asset's unlock requirement.
 */
export const getUnlockDescription = (asset) => {
  const type = asset?.unlock_requirement_type || 'none';
  if (type === 'none') return null;
  if (type === 'xp') return `Reach ${asset?.unlock_xp_required} XP to unlock`;
  if (type === 'badge') return `Earn the "${asset?.unlock_badge_name}" badge to unlock`;
  if (type === 'achievement') return 'Complete a specific achievement to unlock';
  return null;
};

// ─── PRESETS ───────────────────────────────────────────────────────────────────

export const getStudentPresets = async () => {
  const { data, error } = await supabase?.from('avatar_presets')?.select('*')?.eq('is_student_visible', true)?.order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
};

// ─── LAYER RENDERING HELPERS ──────────────────────────────────────────────────

/**
 * Layer order reference:
 *   1  – background
 *   5  – hair_back (virtual, emitted here)
 *   6  – body_main (virtual, emitted here)
 *  20  – clothing
 *  38  – body_head (virtual, emitted here)
 *  40  – eyes
 *  45  – eyebrows
 *  50  – mouths
 *  65  – facial_hair
 *  90  – accessories
 *  92  – hair_front (virtual, emitted here)
 *  95  – fantasy
 *  97  – hats
 *
 * Given an avatar config and a map of assets by category,
 * return an ordered array of { layer_order, publicUrl, category, slug }
 * for rendering the layered avatar preview.
 */
export const buildAvatarLayers = (avatarConfig, assetsByCategorySlug) => {
  const configToCategory = {
    background: 'backgrounds',
    bodyType: 'bodies',
    eyes: 'eyes',
    eyebrows: 'eyebrows',
    mouth: 'mouths',
    hairStyle: 'hair_styles',
    facialHair: 'facial_hair',
    clothing: 'clothing',
    accessory: 'accessories',
    hat: 'hats',
    wings: 'fantasy',
    costume: 'clothing',
    specialHead: 'fantasy',
    effect: 'fantasy',
  };

  const layers = [];

  for (const [configKey, categorySlug] of Object.entries(configToCategory)) {
    const value = avatarConfig?.[configKey];
    if (!value) continue;

    const assets = assetsByCategorySlug?.[categorySlug] || [];
    const asset = assets?.find(a => a?.slug === value || a?.style_variant === value);

    if (asset) {
      // Hair assets are split into two pieces: hair_back and hair_front
      if (categorySlug === 'hair_styles') {
        // hair_back: just above background (layer 5), below body_main (layer 6)
        if (asset?.hair_back_storage_path) {
          const backUrl = asset?.hair_back_public_url || getAssetPublicUrl(asset?.hair_back_storage_path);
          layers?.push({
            layer_order: 5,
            publicUrl: backUrl,
            category: 'hair_styles_back',
            slug: `${asset?.slug}__back`,
            configKey,
          });
        }
        // hair_front: above accessories (layer 92), below fantasy (layer 95)
        layers?.push({
          layer_order: 92,
          publicUrl: asset?.publicUrl || getAssetPublicUrl(asset?.storage_path),
          category: 'hair_styles_front',
          slug: `${asset?.slug}__front`,
          configKey,
        });
      } else if (categorySlug === 'bodies') {
        // body_main: just above hair_back (layer 6), below clothing (layer 20)
        layers?.push({
          layer_order: 6,
          publicUrl: asset?.publicUrl || getAssetPublicUrl(asset?.storage_path),
          category: 'bodies_main',
          slug: `${asset?.slug}__main`,
          configKey,
        });
        // body_head: just behind eyes (layer 38), above clothing (layer 20)
        if (asset?.body_head_storage_path) {
          const headUrl = asset?.body_head_public_url || getAssetPublicUrl(asset?.body_head_storage_path);
          layers?.push({
            layer_order: 38,
            publicUrl: headUrl,
            category: 'bodies_head',
            slug: `${asset?.slug}__head`,
            configKey,
          });
        }
      } else {
        layers?.push({
          layer_order: asset?.layer_order || 0,
          publicUrl: asset?.publicUrl || getAssetPublicUrl(asset?.storage_path),
          category: categorySlug,
          slug: asset?.slug,
          configKey,
        });
      }
    }
  }

  return layers?.sort((a, b) => a?.layer_order - b?.layer_order);
};

// ─── DEFAULT AVATAR CONFIG ────────────────────────────────────────────────────

export const DEFAULT_AVATAR_CONFIG = {
  background: null,
  bodyType: null,
  eyes: null,
  eyeColor: null,
  eyebrows: null,
  mouth: null,
  hairStyle: null,
  hairColor: null,
  facialHair: null,
  clothing: null,
  costume: null,
  hat: null,
  accessory: null,
  wings: null,
  effect: null,
  specialHead: null,
};
